import type { ClientSurveyAnswer, SurveyQuestion } from '../types/survey';

export const isInstructionQuestion = (question: Pick<SurveyQuestion, 'questionType'>): boolean =>
  question.questionType === 'INSTRUCTION';

export const isAnswerableQuestion = (question: Pick<SurveyQuestion, 'questionType'>): boolean =>
  !isInstructionQuestion(question);

export const isDefinedNumericLimit = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

type NumericQuestion = Pick<SurveyQuestion, 'questionType' | 'minValue' | 'maxValue'>;

export function isValidNumericAnswer(
  value: number | null | undefined,
  question: NumericQuestion
): boolean {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;

  if (question.questionType === 'SCALE') {
    if (!Number.isInteger(value)) return false;
    if (!isDefinedNumericLimit(question.minValue) || !isDefinedNumericLimit(question.maxValue)) {
      return false;
    }
    return value >= question.minValue && value <= question.maxValue;
  }

  if (question.questionType === 'NUMBER') {
    if (isDefinedNumericLimit(question.minValue) && value < question.minValue) return false;
    if (isDefinedNumericLimit(question.maxValue) && value > question.maxValue) return false;
    return true;
  }

  return false;
}

export function parseNumericAnswerInput(value: string): number | null {
  if (value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function clampNumericValue(value: number, question: NumericQuestion): number {
  let result = question.questionType === 'SCALE' ? Math.round(value) : value;
  if (isDefinedNumericLimit(question.minValue)) {
    result = Math.max(question.minValue, result);
  }
  if (isDefinedNumericLimit(question.maxValue)) {
    result = Math.min(question.maxValue, result);
  }
  return result;
}

export function formatNumericAnswerDisplay(value: number, question: NumericQuestion): string {
  return question.questionType === 'SCALE' ? String(Math.round(value)) : String(value);
}

export function resolveNumericAnswerFromInput(
  raw: string,
  question: NumericQuestion,
  options?: { clamp?: boolean }
): { numericValue: number | null; displayValue: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { numericValue: null, displayValue: '' };
  const parsed = parseNumericAnswerInput(trimmed);
  if (parsed === null) return { numericValue: null, displayValue: raw };
  const final = options?.clamp ? clampNumericValue(parsed, question) : parsed;
  return {
    numericValue: final,
    displayValue: formatNumericAnswerDisplay(final, question)
  };
}

const getNumericStep = (question: NumericQuestion): number =>
  question.questionType === 'SCALE' ? 1 : 1;

/** Detecta incremento/decremento con las flechas del input numérico. */
export function isLikelySpinnerStepChange(
  previousValue: number | null | undefined,
  nextValue: number,
  question: NumericQuestion
): boolean {
  if (previousValue == null || !Number.isFinite(previousValue)) return false;
  const step = getNumericStep(question);
  return Math.abs(nextValue - previousValue) === step;
}

export function getNumericRangeHelperText(question: NumericQuestion): string | undefined {
  const { questionType, minValue, maxValue } = question;
  const hasMin = isDefinedNumericLimit(minValue);
  const hasMax = isDefinedNumericLimit(maxValue);

  if (questionType === 'SCALE' && hasMin && hasMax) {
    return `Entero entre ${minValue} y ${maxValue}`;
  }
  if (questionType === 'NUMBER') {
    if (hasMin && hasMax) return `Valor entre ${minValue} y ${maxValue}`;
    if (hasMin) return `Valor mínimo: ${minValue}`;
    if (hasMax) return `Valor máximo: ${maxValue}`;
  }
  return undefined;
}

export function getNumericInputProps(
  question: NumericQuestion
): { min?: number; max?: number; step?: number } {
  const props: { min?: number; max?: number; step?: number } = {};
  if (question.questionType === 'SCALE') {
    props.step = 1;
    if (isDefinedNumericLimit(question.minValue)) props.min = question.minValue;
    if (isDefinedNumericLimit(question.maxValue)) props.max = question.maxValue;
  } else if (question.questionType === 'NUMBER') {
    if (isDefinedNumericLimit(question.minValue)) props.min = question.minValue;
    if (isDefinedNumericLimit(question.maxValue)) props.max = question.maxValue;
  }
  return props;
}

/** Props para captura: respeta límites en flechas nativas del navegador. */
export function getNumericCaptureInputProps(
  question: NumericQuestion
): { min?: number; max?: number; step?: number | 'any' } {
  if (question.questionType === 'NUMBER') {
    return { ...getNumericInputProps(question), step: 'any' };
  }
  return getNumericInputProps(question);
}

/** Validación de límites al crear/editar plantillas. */
export function validateQuestionLimitsForTemplate(question: SurveyQuestion): string | null {
  if (question.questionType === 'SCALE') {
    const { minValue, maxValue } = question;
    if (!isDefinedNumericLimit(minValue) || !isDefinedNumericLimit(maxValue)) {
      return `La pregunta "${question.questionText.trim() || '(sin texto)'}" requiere valor mínimo y máximo.`;
    }
    if (!Number.isInteger(minValue) || !Number.isInteger(maxValue)) {
      return `Los límites de escala deben ser enteros en "${question.questionText.trim()}".`;
    }
    if (minValue >= maxValue) {
      return `En escala, el mínimo debe ser menor que el máximo en "${question.questionText.trim()}".`;
    }
    return null;
  }

  if (question.questionType === 'NUMBER') {
    const hasMin = isDefinedNumericLimit(question.minValue);
    const hasMax = isDefinedNumericLimit(question.maxValue);
    if (hasMin && hasMax && question.minValue >= question.maxValue) {
      return `En preguntas numéricas, el mínimo debe ser menor que el máximo en "${question.questionText.trim()}".`;
    }
    return null;
  }

  return null;
}

export function getDefaultScaleLimits(): { minValue: number; maxValue: number } {
  return { minValue: 1, maxValue: 5 };
}

export function getOrderedQuestions(questions: SurveyQuestion[] | undefined): SurveyQuestion[] {
  return [...(questions || [])].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getAnswerableQuestions(questions: SurveyQuestion[] | undefined): SurveyQuestion[] {
  return getOrderedQuestions(questions).filter(isAnswerableQuestion);
}

/** Ajusta respuestas numéricas/escala al rango antes de persistir. */
export function normalizeNumericAnswersForSave(
  answers: ClientSurveyAnswer[],
  questions: SurveyQuestion[]
): ClientSurveyAnswer[] {
  const questionById = new Map(
    questions
      .filter((q) => q.id != null)
      .map((q) => [Number(q.id), q] as const)
  );

  return answers.map((answer) => {
    const question = questionById.get(answer.surveyQuestionId);
    if (
      !question ||
      (question.questionType !== 'NUMBER' && question.questionType !== 'SCALE') ||
      answer.numericValue == null ||
      !Number.isFinite(answer.numericValue)
    ) {
      return answer;
    }
    return {
      ...answer,
      numericValue: clampNumericValue(answer.numericValue, question)
    };
  });
}

/** Excluye respuestas de ítems INSTRUCTION antes de batch-save. */
export function filterAnswersForBatchSave(
  answers: ClientSurveyAnswer[],
  questions: SurveyQuestion[]
): ClientSurveyAnswer[] {
  const instructionIds = new Set(
    questions
      .filter(isInstructionQuestion)
      .map((q) => Number(q.id))
      .filter((id) => Number.isFinite(id))
  );
  return answers.filter((answer) => !instructionIds.has(answer.surveyQuestionId));
}
